import { createContext, useCallback, useContext, useState } from "react";

// Iter 68 — Global compose-session context. Both the Inbox (Triage)
// "Compose" button and the per-task "Send email" button push sessions
// here; the <FloatingComposeRoot> renders all of them in one place.
//
// Up to 3 sessions can exist at once, of which AT MOST 1 is expanded.
// Minimised sessions show as pills along the bottom-right edge.
//
// Session shape:
//   {
//     id,                // synthetic local id (not the backend draft_id)
//     initial: { ... },  // forwarded to RichEmailComposer.initial
//     minimized: bool,
//     title: string,     // header label (e.g. "New message" or subject)
//     onSent?,           // callback when the session sends successfully
//     clientId?,         // pre-selected client (Inbox compose)
//     showClientPicker?, // bool — Inbox style yes, Task style no
//   }

const ComposeContext = createContext({});

let _id = 0;
const newId = () => `cs_${++_id}_${Date.now().toString(36)}`;

export function ComposeProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  // Open a new compose session. If something is currently expanded, that
  // session is minimised first so only the new one is in front.
  const open = useCallback((opts) => {
    const id = newId();
    setSessions((prev) => {
      const minimisedPrev = prev.map((s) => (s.id === expandedId ? { ...s, minimized: true } : s));
      const next = [...minimisedPrev, {
        id,
        minimized: false,
        title: opts?.title || (opts?.initial?.subject || "New message"),
        showClientPicker: opts?.showClientPicker !== false,  // default true
        ...opts,
      }];
      // Cap at 3 sessions total — Gmail's behaviour. Drop the oldest minimised.
      while (next.length > 3) {
        const drop = next.find((s) => s.minimized) || next[0];
        const i = next.findIndex((s) => s.id === drop.id);
        next.splice(i, 1);
      }
      return next;
    });
    setExpandedId(id);
    return id;
  }, [expandedId]);

  const minimise = useCallback((id) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, minimized: true } : s)));
    setExpandedId((cur) => (cur === id ? null : cur));
  }, []);

  // Click a minimised pill — expand it, minimise the previously expanded.
  const expand = useCallback((id) => {
    setSessions((prev) => prev.map((s) => ({
      ...s,
      minimized: s.id === id ? false : (s.id === expandedId ? true : s.minimized),
    })));
    setExpandedId(id);
  }, [expandedId]);

  const close = useCallback((id) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setExpandedId((cur) => (cur === id ? null : cur));
  }, []);

  // Update a session's metadata (e.g. title after subject typed). Called by
  // the inline RichEmailComposer when fields change.
  const update = useCallback((id, patch) => {
    setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  return (
    <ComposeContext.Provider value={{ sessions, expandedId, open, minimise, expand, close, update }}>
      {children}
    </ComposeContext.Provider>
  );
}

export function useCompose() {
  return useContext(ComposeContext);
}
